/**
 * 키비주얼 부가 파일(로고 BI·CI / 레퍼런스 이미지) 선택·표시 패널.
 *
 * <p>등록 화면과 수정 화면이 상황이 다르지만 보이는 모양은 같아야 해서 한 컴포넌트로 둔다.
 * <ul>
 *   <li><b>등록</b>(KeyVisualUploader) — 키비주얼 id 가 아직 없다. 파일을 메모리에 들고 있다가
 *       확정 저장이 성공해 id 를 받은 뒤에 올린다. → {@code files} / {@code onRemoveLocal} 만 준다.</li>
 *   <li><b>수정</b>(EditKeyVisualModal) — id 가 있으므로 고르는 즉시 올린다.
 *       이미 저장된 것은 {@code urls} 로 받아 삭제까지 지원한다.</li>
 * </ul>
 *
 * 두 목록을 한 줄에 같이 보여주되 <b>저장된 것과 아직 안 올린 것을 색으로 구분</b>한다 —
 * 등록 화면에서 "칩이 떴으니 이미 올라간 것" 으로 오해하면 확정 저장을 안 누르고 나가버린다.
 */
import React, { useRef } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import ActionButton from "../../../../component/common/ActionButton";
import { ds } from "../../../../styles/designSystem";
import { formatFileSize } from "../../../../util/numberFormat";

/** URL 또는 S3 key 에서 사람이 읽을 이름만 뽑는다. 키의 마지막 조각이 곧 표시명이다. */
export function assetDisplayName(urlOrKey) {
    const s = String(urlOrKey || "");
    const noQuery = s.split("?")[0];
    const seg = noQuery.substring(noQuery.lastIndexOf("/") + 1);
    try {
        return decodeURIComponent(seg) || seg;
    } catch {
        return seg;   // 인코딩이 깨진 값이어도 화면은 떠야 한다
    }
}

export default function AssetPicker({
    label,
    hint,
    /** 서버에 저장된 파일 URL 목록 */
    urls = [],
    /** 아직 업로드하지 않은 File 목록 (등록 화면) */
    files = [],
    onFilesAdded,
    onRemoveLocal,
    onRemoveUrl,
    disabled = false,
    accept = "image/*",
}) {
    const inputRef = useRef(null);

    const handleChange = (e) => {
        const picked = Array.from(e.target.files || []);
        if (picked.length > 0) onFilesAdded?.(picked);
        // 같은 파일을 연속으로 고를 수 있게 초기화 — 안 하면 두 번째 선택에서 change 가 안 뜬다.
        e.target.value = "";
    };

    const total = urls.length + files.length;

    return (
        <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: "text.secondary" }}>
                    {label}
                </Typography>
                {total > 0 && (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: ds.colors.primary }}>
                        {total}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <ActionButton
                    variant="secondary"
                    startIcon={false}
                    disabled={disabled}
                    onClick={() => inputRef.current?.click()}
                    sx={{ fontSize: 12 }}
                    label="파일 선택"
                />
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={accept}
                    hidden
                    onChange={handleChange}
                />
            </Stack>

            {hint && (
                <Typography sx={{ fontSize: 11.5, color: "text.secondary", mb: 0.75 }}>
                    {hint}
                </Typography>
            )}

            {total === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
                    첨부된 파일이 없습니다
                </Typography>
            ) : (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {/* 저장된 것 */}
                    {urls.map((u) => (
                        <Chip
                            key={u}
                            size="small"
                            icon={<ImageRoundedIcon sx={{ fontSize: 14 }} />}
                            label={assetDisplayName(u)}
                            onClick={() => window.open(u, "_blank", "noopener,noreferrer")}
                            onDelete={onRemoveUrl && !disabled ? () => onRemoveUrl(u) : undefined}
                            sx={{ fontSize: 12, maxWidth: 260 }}
                        />
                    ))}
                    {/* 아직 안 올린 것 — 점선 테두리로 "미저장" 을 드러낸다 */}
                    {files.map((f, i) => (
                        <Chip
                            key={`${f.name}-${i}`}
                            size="small"
                            icon={<ImageRoundedIcon sx={{ fontSize: 14 }} />}
                            label={`${f.name} (${formatFileSize(f.size)})`}
                            onDelete={onRemoveLocal && !disabled ? () => onRemoveLocal(i) : undefined}
                            sx={{
                                fontSize: 12, maxWidth: 260,
                                bgcolor: "transparent",
                                border: `1px dashed ${ds.colors.primaryBorder}`,
                                color: ds.colors.primaryText,
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Box>
    );
}
